import { PowerService } from "@/infcode/power/service" // infcode_change
import { PowerConfig } from "@/infcode/power/types" // infcode_change
import * as Log from "@opencode-ai/core/util/log"
import path from "path"
// import { pathToFileURL } from "url" //infcode_change
import os from "os"
import z from "zod"
import { mergeDeep } from "remeda"
import { Global } from "@opencode-ai/core/global"
// import fsNode from "fs/promises" //infcode_change
import { NamedError } from "@opencode-ai/core/util/error"
import { Flag } from "@opencode-ai/core/flag/flag"
import { Auth } from "../auth"
import { Env } from "../env"
import { applyEdits, findNodeAtLocation, modify, parseTree } from "jsonc-parser" // kilocode_change - parseTree/findNodeAtLocation used in patchJsonc
import { type InstanceContext } from "../project/instance"
import { InstanceStore } from "../project/instance-store"
import { InstallationLocal, InstallationVersion } from "@opencode-ai/core/installation/version"
// import { existsSync } from "fs" //infcode_change
import { GlobalBus } from "@/bus/global"
import { Event } from "../server/event"
import { Account } from "@/account/account"
import { isRecord } from "@/util/record"
import type { ConsoleState } from "./console-state"
import { AppFileSystem } from "@opencode-ai/core/filesystem"
import { InstanceState } from "@/effect/instance-state"
import { Context, Duration, Effect, Exit, Fiber, Layer, Option, Schema, Semaphore } from "effect" // infcode_change
import { EffectFlock } from "@opencode-ai/core/util/effect-flock"
import { containsPath } from "../project/instance-context"
import { LocalContext } from "@/util/local-context" // infcode_change
import { zod } from "@/util/effect-zod"
import { NonNegativeInt, PositiveInt, withStatics, type DeepMutable } from "@/util/schema"
import { ConfigAgent } from "./agent"
import { ConfigCommand } from "./command"
import { ConfigFormatter } from "./formatter"
import { ConfigLayout } from "./layout"
import { ConfigLSP } from "./lsp"
import { ConfigManaged } from "./managed"
import { ConfigMCP } from "./mcp"
import { ConfigModelID } from "./model-id"
import { ConfigParse } from "./parse"
import { ConfigPaths } from "./paths"
import { ConfigPermission } from "./permission"
import { ConfigPlugin } from "./plugin"
import { ConfigProvider } from "./provider"
import { ConfigServer } from "./server"
import { ConfigSkills } from "./skills"
import { ConfigVariable } from "./variable"
import { ConfigError } from "./error" //infcode_change
import { Npm } from "@opencode-ai/core/npm"
// kilocode_change start
import { ZodOverride } from "@/util/effect-zod"
// import { KilocodeConfig } from "../kilocode/config/config" //infcode_change
import { KilocodeDefaultPlugins } from "@/kilocode/config/default-plugins"
import { InfcodeMcpConfig } from "@/infcode/mcp/config" //infcode_change
import { TerminalRunSettingsStore } from "@/kilocode/config/terminal-run-settings-store" // infcode_change
import { CodeReviewSettingsStore } from "@/infcode/code-review/settings" // infcode_change
import { IndexingConfig as KiloIndexingConfig } from "@kilocode/kilo-indexing/config"
import { makeRuntime } from "@/effect/run-service"
import { unique } from "remeda"
// kilocode_change end

const log = Log.create({ service: "config" })

// Custom merge function that concatenates array fields instead of replacing them
// Keep remeda's deep conditional merge type out of hot config-loading paths; TS profiling showed it dominates here.
function mergeConfig(target: Info, source: Info): Info {
  return mergeDeep(target, source) as Info
}

function mergeConfigConcatArrays(target: Info, source: Info): Info {
  const merged = mergeConfig(target, source)
  if (target.instructions && source.instructions) {
    merged.instructions = Array.from(new Set([...target.instructions, ...source.instructions]))
  }
  return merged
}

function normalizeLoadedConfig(data: unknown, source: string) {
  if (!isRecord(data)) return data
  const copy = { ...data }
  const hadLegacy = "theme" in copy || "keybinds" in copy || "tui" in copy
  if (!hadLegacy) return copy
  delete copy.theme
  delete copy.keybinds
  delete copy.tui
  log.warn("tui keys in opencode config are deprecated; move them to tui.json", { path: source })
  return copy
}

// kilocode_change start
export const Warning = z.object({
  path: z.string(),
  message: z.string(),
  detail: z.string().optional(),
})
export type Warning = z.infer<typeof Warning>

// infcode_change start
function formatIssues(issues: z.core.$ZodIssue[]) {
  return issues
    .map((issue) => {
      const loc = issue.path.map(String).join(".")
      if (!loc) return issue.message
      return `${loc}: ${issue.message}`
    })
    .join("\n")
}

function caughtWarning(warnings: Warning[], source: string, err: unknown) {
  if (ConfigError.JsonError.isInstance(err)) {
    warnings.push({
      path: err.data.path,
      message: `Config file at ${err.data.path} is not valid JSON(C)`,
      detail: err.data.message || undefined,
    })
    log.warn("skipped config due to error", { source, err })
    return
  }
  if (ConfigError.InvalidError.isInstance(err)) {
    const text = err.data.issues ? formatIssues(err.data.issues) : err.data.message
    warnings.push({
      path: err.data.path,
      message: text
        ? `Configuration is invalid at ${err.data.path}: ${text}`
        : `Configuration is invalid at ${err.data.path}`,
    })
    log.warn("skipped config due to error", { source, err })
    return
  }
  throw err
}
//infcode_change_end
// kilocode_change end

async function resolveLoadedPlugins<T extends { plugin?: ConfigPlugin.Spec[] }>(config: T, filepath: string) {
  if (!config.plugin) return config
  for (let i = 0; i < config.plugin.length; i++) {
    // Normalize path-like plugin specs while we still know which config file declared them.
    // This prevents `./plugin.ts` from being reinterpreted relative to some later merge location.
    config.plugin[i] = await ConfigPlugin.resolvePluginSpec(config.plugin[i], filepath)
  }
  return config
}

export const Server = ConfigServer.Server.zod
export const Layout = ConfigLayout.Layout.zod
export type Layout = ConfigLayout.Layout

// kilocode_change start - indexing configuration
export const Indexing = KiloIndexingConfig
export type Indexing = z.infer<typeof Indexing>
// kilocode_change end

const LogLevelRef = Schema.Literals(["DEBUG", "INFO", "WARN", "ERROR"]).annotate({
  identifier: "LogLevel",
  description: "Log level",
})

// infcode_change start - keep the Effect/OpenAPI config contract concrete while
// preserving the original Zod parser used by config loading.
const optionalString = Schema.optional(Schema.String)
const apiKeyOptions = Schema.Struct({ apiKey: optionalString })
const IndexingRef = Schema.Struct({
  enabled: Schema.optional(Schema.Boolean).annotate({ description: "Enable codebase indexing" }),
  provider: Schema.optional(
    Schema.Literals([
      "kilo",
      "openai",
      "ollama",
      "openai-compatible",
      "gemini",
      "mistral",
      "vercel-ai-gateway",
      "bedrock",
      "openrouter",
      "voyage",
    ]),
  ).annotate({ description: "Embedding provider to use for codebase indexing" }),
  model: optionalString.annotate({ description: "Embedding model ID (uses provider default if omitted)" }),
  dimension: Schema.optional(Schema.Int.check(Schema.isGreaterThan(0))).annotate({
    description: "Override embedding vector dimension (auto-detected from model if omitted)",
  }),
  vectorStore: Schema.optional(Schema.Literals(["lancedb", "qdrant"])).annotate({
    description: "Vector store backend (default: qdrant)",
  }),
  kilo: Schema.optional(
    Schema.Struct({ apiKey: optionalString, baseUrl: optionalString, organizationId: optionalString }),
  ).annotate({ description: "Kilo-hosted embedding provider options" }),
  openai: Schema.optional(apiKeyOptions).annotate({ description: "OpenAI embedding provider options" }),
  ollama: Schema.optional(Schema.Struct({ baseUrl: optionalString })).annotate({
    description: "Ollama embedding provider options",
  }),
  "openai-compatible": Schema.optional(Schema.Struct({ baseUrl: optionalString, apiKey: optionalString })).annotate({
    description: "OpenAI-compatible embedding provider options",
  }),
  gemini: Schema.optional(apiKeyOptions).annotate({ description: "Gemini embedding provider options" }),
  mistral: Schema.optional(apiKeyOptions).annotate({ description: "Mistral embedding provider options" }),
  "vercel-ai-gateway": Schema.optional(apiKeyOptions).annotate({
    description: "Vercel AI Gateway embedding provider options",
  }),
  bedrock: Schema.optional(Schema.Struct({ region: optionalString, profile: optionalString })).annotate({
    description: "AWS Bedrock embedding provider options",
  }),
  openrouter: Schema.optional(Schema.Struct({ apiKey: optionalString, specificProvider: optionalString })).annotate({
    description: "OpenRouter embedding provider options",
  }),
  voyage: Schema.optional(apiKeyOptions).annotate({ description: "Voyage embedding provider options" }),
  qdrant: Schema.optional(Schema.Struct({ url: optionalString, apiKey: optionalString })).annotate({
    description: "Qdrant vector store connection options",
  }),
  lancedb: Schema.optional(Schema.Struct({ directory: optionalString })).annotate({
    description: "LanceDB vector store options",
  }),
  searchMinScore: Schema.optional(Schema.Finite.check(Schema.isBetween({ minimum: 0, maximum: 1 }))).annotate({
    description: "Minimum similarity score for search results (default: 0.4)",
  }),
  searchMaxResults: Schema.optional(Schema.Int.check(Schema.isGreaterThan(0))).annotate({
    description: "Maximum number of search results (default: 50)",
  }),
  embeddingBatchSize: Schema.optional(Schema.Int.check(Schema.isGreaterThan(0))).annotate({
    description: "Number of code segments per embedding batch (default: 60)",
  }),
  scannerMaxBatchRetries: Schema.optional(Schema.Int.check(Schema.isGreaterThan(0))).annotate({
    description: "Maximum retry attempts for failed embedding batches (default: 3)",
  }),
}).annotate({ identifier: "IndexingConfig", [ZodOverride]: KiloIndexingConfig })
// infcode_change end

//infcode_change_start
const CommitMessageSchema = Schema.optional(
  Schema.Struct({
    prompt: Schema.optional(Schema.String).annotate({
      description:
        "Custom system prompt for AI commit message generation. When set, replaces the default conventional commits prompt entirely.",
    }),
  }),
).annotate({ description: "Configuration for AI-generated commit messages" })

// infcode_change start
const TerminalRunRuntime = Schema.Struct({
  autoApprove: Schema.Boolean,
  askList: Schema.optional(Schema.mutable(Schema.Array(Schema.String))),
}).annotate({
  description:
    "Terminal command approval settings. Opencode persists this shape and converts it to permission.terminal_run rules.",
})

const CodeReviewDimension = Schema.Literals([
  "business",
  "security",
  "performance",
  "error_handling",
  "dependency",
  "testing",
  "maintainability",
  "documentation",
  "readability",
  "consistency",
])

const CodeReviewConfig = Schema.Struct({
  dimensions: Schema.optional(Schema.mutable(Schema.Array(CodeReviewDimension))).annotate({
    description: "Code Review dimensions enabled for Review mode. Defaults to 6 core dimensions when omitted.",
  }),
}).annotate({ description: "InfCode Code Review mode configuration" })
// infcode_change end

function scopeIndexing(info: Info, scope: ConfigPlugin.Scope): Info {
  if (scope !== "global") return info
  return stripGlobalIndexing(info)
}

function stripGlobalIndexing(info: Info): Info {
  if (info.indexing?.enabled === undefined) return info
  const indexing = Object.fromEntries(Object.entries(info.indexing).filter(([key]) => key !== "enabled"))
  if (Object.keys(indexing).length > 0) return { ...info, indexing }
  const copy = { ...info }
  delete copy.indexing
  return copy
}
// infcode_change end

// The Effect Schema is the canonical source of truth. The `.zod` compatibility
// surface is derived so existing Hono validators keep working without a parallel
// Zod definition.
//
// The walker emits `z.object({...})` which is non-strict by default. Config
// historically uses `.strict()` (additionalProperties: false in openapi.json),
// so layer that on after derivation.  Re-apply the Config ref afterward
// since `.strict()` strips the walker's meta annotation.
export const Info = Schema.Struct({
  $schema: Schema.optional(Schema.String).annotate({
    description: "JSON schema reference for configuration validation",
  }),
  shell: Schema.optional(Schema.String).annotate({
    description: "Default shell to use for terminal and bash tool",
  }),
  logLevel: Schema.optional(LogLevelRef).annotate({ description: "Log level" }),
  server: Schema.optional(ConfigServer.Server).annotate({
    description: "Server configuration for opencode serve and web commands",
  }),
  command: Schema.optional(Schema.Record(Schema.String, ConfigCommand.Info)).annotate({
    description: "Command configuration, see https://opencode.ai/docs/commands",
  }),
  skills: Schema.optional(ConfigSkills.Info).annotate({ description: "Additional skill folder paths" }),
  watcher: Schema.optional(
    Schema.Struct({
      ignore: Schema.optional(Schema.mutable(Schema.Array(Schema.String))),
    }),
  ),
  snapshot: Schema.optional(Schema.Boolean).annotate({
    description:
      "Enable or disable snapshot tracking. When false, filesystem snapshots are not recorded and undoing or reverting will not undo/redo file changes. Defaults to true.",
  }),
  // User-facing plugin config is stored as Specs; provenance gets attached later while configs are merged.
  plugin: Schema.optional(Schema.mutable(Schema.Array(ConfigPlugin.Spec))),
  share: Schema.optional(Schema.Literals(["manual", "auto", "disabled"])).annotate({
    description:
      "Control sharing behavior:'manual' allows manual sharing via commands, 'auto' enables automatic sharing, 'disabled' disables all sharing",
  }),
  autoshare: Schema.optional(Schema.Boolean).annotate({
    description: "@deprecated Use 'share' field instead. Share newly created sessions automatically",
  }),
  autoupdate: Schema.optional(Schema.Union([Schema.Boolean, Schema.Literal("notify")])).annotate({
    description:
      "Automatically update to the latest version. Set to true to auto-update, false to disable, or 'notify' to show update notifications",
  }),
  disabled_providers: Schema.optional(Schema.mutable(Schema.Array(Schema.String))).annotate({
    description: "Disable providers that are loaded automatically",
  }),
  enabled_providers: Schema.optional(Schema.mutable(Schema.Array(Schema.String))).annotate({
    description: "When set, ONLY these providers will be enabled. All other providers will be ignored",
  }),
  // kilocode_change start
  // NOTE: Any new kilocode_change key added to Config.Info must also be mirrored in
  // apps/web/src/app/config.json/extras.ts in the cloud repo, otherwise
  remote_control: Schema.optional(Schema.Boolean).annotate({
    description: "Enable remote control of sessions via InfCode Cloud.",
  }),
  auto_collapse_reasoning: Schema.optional(Schema.Boolean).annotate({
    description: "Automatically collapse reasoning blocks after the agent finishes writing them",
  }),
  indexing: Schema.optional(IndexingRef).annotate({ description: "Codebase indexing configuration" }), // kilocode_change
  terminal_command_display: Schema.optional(Schema.Literals(["expanded", "collapsed"])).annotate({
    description: "Controls whether terminal command blocks are expanded or collapsed by default in the VS Code chat UI",
  }),
  terminalRun: Schema.optional(TerminalRunRuntime), // infcode_change
  codeReview: Schema.optional(CodeReviewConfig), // infcode_change
  // infcode_change start - switch the default base system prompt between default.txt ("default")
  // and the Claude-Code-style default-claude.txt ("default-claude"). Only affects the fallback prompt;
  // models with explicit prompt metadata keep their own. The description annotation sits inside
  // Schema.optional — an annotation on the optional wrapper is dropped by JSON Schema / SDK generation.
  systemPromptVariant: Schema.optional(
    Schema.Literals(["default", "default-claude"]).annotate({
      description:
        "Base system prompt used for models without explicit prompt metadata: 'default' or 'default-claude' (Claude Code style); defaults to 'default-claude'",
    }),
  ),
  // infcode_change end
  power: Schema.optional(PowerConfig), // infcode_change - execution-host OS-user preference
  // kilocode_change end
  // kilocode_change start - nullable for delete sentinel
  model: Schema.optional(Schema.NullOr(ConfigModelID)).annotate({
    description: "Model to use in the format of provider/model, eg anthropic/claude-2",
  }),
  small_model: Schema.optional(Schema.NullOr(ConfigModelID)).annotate({
    description: "Small model to use for tasks like title generation in the format of provider/model",
  }),
  // kilocode_change end
  // kilocode_change start - renamed from "build" to "code" + nullable for delete sentinel
  default_agent: Schema.optional(Schema.NullOr(Schema.String)).annotate({
    description:
      "Default agent to use when none is specified. Must be a primary agent. Falls back to 'code' if not set or if the specified agent is invalid.",
  }),
  // kilocode_change end
  username: Schema.optional(Schema.String).annotate({
    description: "Custom username to display in conversations instead of system username",
  }),
  mode: Schema.optional(
    Schema.StructWithRest(
      Schema.Struct({
        build: Schema.optional(ConfigAgent.Info),
        plan: Schema.optional(ConfigAgent.Info),
      }),
      [Schema.Record(Schema.String, ConfigAgent.Info)],
    ),
  ).annotate({ description: "@deprecated Use `agent` field instead." }),
  agent: Schema.optional(
    Schema.StructWithRest(
      Schema.Struct({
        // primary
        plan: Schema.optional(ConfigAgent.Info),
        build: Schema.optional(ConfigAgent.Info),
        debug: Schema.optional(ConfigAgent.Info), // kilocode_change
        orchestrator: Schema.optional(ConfigAgent.Info), // kilocode_change
        ask: Schema.optional(ConfigAgent.Info), // kilocode_change
        review: Schema.optional(ConfigAgent.Info), // infcode_change
        // subagent
        general: Schema.optional(ConfigAgent.Info),
        explore: Schema.optional(ConfigAgent.Info),
        // specialized
        title: Schema.optional(ConfigAgent.Info),
        summary: Schema.optional(ConfigAgent.Info),
        compaction: Schema.optional(ConfigAgent.Info),
      }),
      [Schema.Record(Schema.String, ConfigAgent.Info)],
    ),
  ).annotate({ description: "Agent configuration, see https://opencode.ai/docs/agents" }),
  provider: Schema.optional(Schema.Record(Schema.String, Schema.NullOr(ConfigProvider.Info))).annotate({
    // kilocode_change - nullable for delete sentinel
    description: "Custom provider configurations and model overrides",
  }),
  mcp: Schema.optional(
    Schema.Record(
      Schema.String,
      Schema.Union([
        ConfigMCP.Info,
        // Matches the legacy `{ enabled: false }` form used to disable a server.
        Schema.Struct({ enabled: Schema.Boolean }),
      ]),
    ),
  ).annotate({ description: "MCP (Model Context Protocol) server configurations" }),
  formatter: Schema.optional(ConfigFormatter.Info),
  lsp: Schema.optional(ConfigLSP.Info),
  instructions: Schema.optional(Schema.mutable(Schema.Array(Schema.String))).annotate({
    description: "Additional instruction files or patterns to include",
  }),
  layout: Schema.optional(ConfigLayout.Layout).annotate({ description: "@deprecated Always uses stretch layout." }),
  permission: Schema.optional(ConfigPermission.Info),
  tools: Schema.optional(Schema.Record(Schema.String, Schema.Boolean)),
  enterprise: Schema.optional(
    Schema.Struct({
      url: Schema.optional(Schema.String).annotate({ description: "Enterprise URL" }),
    }),
  ),
  commit_message: CommitMessageSchema, // kilocode_change //infcode_change
  tool_output: Schema.optional(
    Schema.Struct({
      max_lines: Schema.optional(PositiveInt).annotate({
        description: "Maximum lines of tool output before it is truncated and saved to disk (default: 2000)",
      }),
      max_bytes: Schema.optional(PositiveInt).annotate({
        description: "Maximum bytes of tool output before it is truncated and saved to disk (default: 51200)",
      }),
    }),
  ).annotate({
    description:
      "Thresholds for truncating tool output. When output exceeds either limit, the full text is written to the truncation directory and a preview is returned.",
  }),
  compaction: Schema.optional(
    Schema.Struct({
      auto: Schema.optional(Schema.Boolean).annotate({
        description: "Enable automatic compaction when context is full (default: true)",
      }),
      prune: Schema.optional(Schema.Boolean).annotate({
        description: "Enable pruning of old tool outputs and stored input expansions across cleanup paths (default: false)", // infcode_change
      }),
      tail_turns: Schema.optional(NonNegativeInt).annotate({
        description:
          "Number of recent user turns, including their following assistant/tool responses, to keep verbatim during compaction (default: 2)",
      }),
      preserve_recent_tokens: Schema.optional(NonNegativeInt).annotate({
        description: "Maximum number of tokens from recent turns to preserve verbatim after compaction",
      }),
      reserved: Schema.optional(NonNegativeInt).annotate({
        description: "Token buffer for compaction. Leaves enough window to avoid overflow during compaction.",
      }),
    }),
  ),
  experimental: Schema.optional(
    Schema.Struct({
      disable_paste_summary: Schema.optional(Schema.Boolean),
      batch_tool: Schema.optional(Schema.Boolean).annotate({ description: "Enable the batch tool" }),
      codebase_search: Schema.optional(Schema.Boolean).annotate({ description: "Enable AI-powered codebase search" }), // kilocode_change
      // kilocode_change start
      semantic_indexing: Schema.optional(Schema.Boolean).annotate({
        description: "Enable semantic codebase indexing and the semantic_search tool",
      }),
      agent_manager_tool: Schema.optional(Schema.Boolean).annotate({
        description: "Enable the VS Code Agent Manager orchestration tool",
      }),
      // infcode_change start - deferred tools / tool_search kill switch (2026-09-14 spec D4)
      tool_search: Schema.optional(Schema.Boolean).annotate({
        description:
          "Enable the deferred tool pool and the tool_search meta tool (default: off = byte-identical legacy tool exposure)",
      }),
      // infcode_change start - todo projection kill switch (todo-projection spec, 与 tool_search 同构)
      todo_projection: Schema.optional(Schema.Boolean).annotate({
        description:
          "Inject a bounded <todo_state> projection of non-terminal todos as a transient tail block (default: off = byte-identical legacy requests)",
      }),
      // infcode_change end
      // kilocode_change end
      // kilocode_change start - enable telemetry by default
      openTelemetry: Schema.Boolean.pipe(Schema.optional, Schema.withDecodingDefault(Effect.succeed(true))).annotate({
        description: "Enable telemetry. Set to false to opt-out.",
      }),
      // kilocode_change end
      primary_tools: Schema.optional(Schema.mutable(Schema.Array(Schema.String))).annotate({
        description: "Tools that should only be available to primary agents.",
      }),
      continue_loop_on_deny: Schema.optional(Schema.Boolean).annotate({
        description: "Continue the agent loop when a tool call is denied",
      }),
      mcp_timeout: Schema.optional(PositiveInt).annotate({
        description: "Timeout in milliseconds for model context protocol (MCP) requests",
      }),
    }),
  ),
})
  .annotate({ identifier: "Config" })
  .pipe(
    withStatics((s) => ({
      zod: (zod(s) as unknown as z.ZodObject<any>).strict().meta({ ref: "Config" }) as unknown as z.ZodType<
        DeepMutable<Schema.Schema.Type<typeof s>>
      >,
    })),
  )

// Uses the shared `DeepMutable` from `@/util/schema`. See the definition
// there for why the local variant is needed over `Types.DeepMutable` from
// effect-smol (the upstream version collapses `unknown` to `{}`).
export type Info = DeepMutable<Schema.Schema.Type<typeof Info>> & {
  // plugin_origins is derived state, not a persisted config field. It keeps each winning plugin spec together
  // with the file and scope it came from so later runtime code can make location-sensitive decisions.
  plugin_origins?: ConfigPlugin.Origin[]
}

type State = {
  revision: number // infcode_change
  refresh: Semaphore.Semaphore // infcode_change
  config: Info
  directories: string[]
  deps: Fiber.Fiber<void, never>[]
  warnings: Warning[] // kilocode_change
  consoleState: ConsoleState
}

export interface Interface {
  readonly get: () => Effect.Effect<Info>
  readonly getGlobal: () => Effect.Effect<Info>
  readonly getConsoleState: () => Effect.Effect<ConsoleState>
  readonly update: (config: Info, options?: { dispose?: boolean }) => Effect.Effect<void>
  readonly updateGlobal: (config: Info, options?: { dispose?: boolean }) => Effect.Effect<Info> // kilocode_change
  readonly invalidate: (wait?: boolean) => Effect.Effect<void>
  readonly directories: () => Effect.Effect<string[]>
  readonly waitForDependencies: () => Effect.Effect<void>
  readonly warnings: () => Effect.Effect<Warning[]> // kilocode_change
}

export class Service extends Context.Service<Service, Interface>()("@opencode/Config") {}

//infcode_change_start
// function globalConfigFile() {
//   // kilocode_change start
//   const candidates = ["kilo.jsonc", "kilo.json", "opencode.jsonc", "opencode.json", "config.json"].map((file) =>
//     // kilocode_change end
//     path.join(Global.Path.config, file),
//   )
//   for (const file of candidates) {
//     if (existsSync(file)) return file
//   }
//   return candidates[0]
// }
//infcode_change_end

function patchJsonc(input: string, patch: unknown, path: string[] = []): string {
  if (!isRecord(patch)) {
    // kilocode_change - null means "delete this key" — pass undefined to jsonc-parser's modify()
    const edits = modify(input, path, patch === null ? undefined : patch, {
      formattingOptions: {
        insertSpaces: true,
        tabSize: 2,
      },
    })
    return applyEdits(input, edits)
  }

  // kilocode_change start — when the existing JSONC node at this path is a
  // scalar (e.g. permission.bash is "ask" as a string), jsonc-parser cannot
  // add child keys to it. Detect this case and replace the whole node with
  // the patch object in a single modify() call instead of recursing.
  // For permission keys, promote the scalar to { "*": scalarValue } so the
  // wildcard default is preserved. For other keys, replace directly.
  if (path.length > 0) {
    const tree = parseTree(input)
    const node = tree && findNodeAtLocation(tree, path)
    if (node && node.type !== "object") {
      const isPermissionKey = path[0] === "permission" && path.length === 2
      const replacement = isPermissionKey ? { "*": node.value, ...patch } : patch
      const edits = modify(input, path, replacement, {
        formattingOptions: { insertSpaces: true, tabSize: 2 },
      })
      return applyEdits(input, edits)
    }
  }
  // kilocode_change end

  return Object.entries(patch).reduce((result, [key, value]) => patchJsonc(result, value, [...path, key]), input)
}

function writable(info: Info) {
  const { plugin_origins: _plugin_origins, ...next } = info
  return next
} // infcode_change

function writableGlobal(info: Info) {
  const next = writable(info)
  // When a user changes config from a value back to default in the Desktop app, we don't want to leave a blank `"shell": "",` key
  if ("shell" in next && next.shell === "") return { ...next, shell: undefined }
  return next
}

function hasOwn<T extends object>(target: T, key: PropertyKey): boolean {
  return Object.prototype.hasOwnProperty.call(target, key)
}

let runtimeTerminalRunPermission: ConfigPermission.Rule | undefined
let hasRuntimeTerminalRunPermission = false
// infcode_change start - shared across Config layer instances so dispose:false updates
// invalidate lazy per-directory consumers owned by AppRuntime and legacy runtimes alike.
let configRevision = 0

export function revision() {
  return configRevision
}

function bumpRevision() {
  configRevision += 1
}
// infcode_change end

function terminalRunPermissionPatch(info: Info): Info | undefined {
  const permission = info.permission
  if (!permission || typeof permission !== "object" || !hasOwn(permission, "terminal_run")) return undefined
  return { permission: { terminal_run: permission.terminal_run } } as Info
}

function terminalRunRuntimePatch(info: Info): Info | undefined {
  if (!hasOwn(info, "terminalRun")) return undefined
  return { terminalRun: info.terminalRun } as Info
}

function stripTerminalRunRuntime(info: Info): Info {
  if (!hasOwn(info, "terminalRun")) return info
  const { terminalRun: _terminalRun, ...rest } = info
  return rest as Info
}

function terminalRunAskList(items: readonly string[] | undefined): string[] {
  return unique((items ?? []).map((item) => item.trim()).filter(Boolean))
}

function terminalRunAskPatterns(pattern: string): string[] {
  const safePattern = collapseRepeatedWildcards(pattern)
  if (safePattern.includes("*")) return [safePattern]
  return [safePattern, `${safePattern} *`]
}

function collapseRepeatedWildcards(pattern: string): string {
  let collapsed = ""
  let previousWasWildcard = false
  for (const char of pattern) {
    if (char === "*") {
      if (!previousWasWildcard) collapsed += char
      previousWasWildcard = true
      continue
    }
    collapsed += char
    previousWasWildcard = false
  }
  return collapsed
}

export function terminalRunPermissionFromRuntime(runtime: NonNullable<Info["terminalRun"]>): ConfigPermission.Rule { // infcode_change - reuse the pure terminal policy compiler for Root restrictions
  if (!runtime.autoApprove) return "ask"
  const items = terminalRunAskList(runtime.askList).flatMap(terminalRunAskPatterns)
  return {
    "*": "allow",
    ...Object.fromEntries(items.map((item) => [item, "ask" as const])),
  }
}

function setRuntimeTerminalRunPermission(info: Info) {
  const patch = terminalRunPermissionPatch(info)
  if (!patch) return
  hasRuntimeTerminalRunPermission = true
  runtimeTerminalRunPermission = patch.permission?.terminal_run
}

function setRuntimeTerminalRunConfig(info: Info) {
  const patch = terminalRunRuntimePatch(info)
  if (!patch?.terminalRun) return false
  hasRuntimeTerminalRunPermission = true
  runtimeTerminalRunPermission = terminalRunPermissionFromRuntime(patch.terminalRun)
  return true
}

function ensureRuntimeTerminalRunPermission() {
  if (hasRuntimeTerminalRunPermission) return
  const terminalRun = TerminalRunSettingsStore.getOrCreateDefault() // infcode_change
  hasRuntimeTerminalRunPermission = true
  runtimeTerminalRunPermission = terminalRunPermissionFromRuntime(terminalRun) // infcode_change
}

export function runtimeTerminalRunPermissionConfig(): ConfigPermission.Info {
  ensureRuntimeTerminalRunPermission()
  return { terminal_run: runtimeTerminalRunPermission } as ConfigPermission.Info
}

function runtimeTerminalRunPermissionInfo(): Info {
  ensureRuntimeTerminalRunPermission()
  return { permission: runtimeTerminalRunPermissionConfig() } as Info
}

function terminalRunPermissionFromInfo(info: Info): Info | undefined {
  const patch = terminalRunRuntimePatch(info)
  if (!patch?.terminalRun) return undefined
  return { permission: { terminal_run: terminalRunPermissionFromRuntime(patch.terminalRun) } } as Info
}

function terminalRunPermissionOnly(info: Info): boolean {
  const patch = terminalRunPermissionPatch(info)
  if (!patch) return false
  return Object.keys(info).length === 1 && Object.keys(info.permission ?? {}).length === 1
}

function terminalRunRuntimeOnly(info: Info): boolean {
  const patch = terminalRunRuntimePatch(info)
  if (!patch) return false
  return Object.keys(info).length === 1
}

function codeReviewOnly(info: Info): boolean {
  return hasOwn(info, "codeReview") && Object.keys(info).length === 1
}

function hasIgnoredGlobalPatch(info: Info): boolean {
  if (Object.keys(info).some((key) => key !== "permission" && key !== "codeReview" && key !== "power")) return true
  const permission = info.permission
  if (!permission || typeof permission !== "object") return false
  return Object.keys(permission).some((key) => key !== "terminal_run")
}

function mergeRuntimeGlobal(target: Info, patch: Info): Info {
  const next = mergeConfig(target, patch)
  const terminal =
    terminalRunPermissionPatch(patch) ?? terminalRunPermissionFromInfo(target) ?? runtimeTerminalRunPermissionInfo()
  if (Object.keys(terminal).length > 0) {
    next.permission ??= {}
    next.permission.terminal_run = terminal.permission?.terminal_run
  }
  return next
}

function ignoreMissingInstance<A, E, R>(self: Effect.Effect<A, E, R>): Effect.Effect<A | void, E, R> {
  return self.pipe(
    Effect.catchDefect((err) => {
      if (err instanceof LocalContext.NotFound) return Effect.void
      return Effect.die(err)
    }),
  )
}
// infcode_change end

export const ConfigDirectoryTypoError = NamedError.create(
  "ConfigDirectoryTypoError",
  z.object({
    path: z.string(),
    dir: z.string(),
    suggestion: z.string(),
  }),
)

export const layer = Layer.effect(
  Service,
  Effect.gen(function* () {
    const fs = yield* AppFileSystem.Service
    const authSvc = yield* Auth.Service
    const accountSvc = yield* Account.Service
    const env = yield* Env.Service
    const npmSvc = yield* Npm.Service

    const readConfigFile = Effect.fnUntraced(function* (filepath: string) {
      return yield* fs.readFileString(filepath).pipe(
        Effect.catchIf(
          (e) => e.reason._tag === "NotFound",
          () => Effect.succeed(undefined),
        ),
        Effect.orDie,
      )
    })

    const loadConfig = Effect.fnUntraced(function* (
      text: string,
      options: { path: string } | { dir: string; source: string },
    ) {
      const source = "path" in options ? options.path : options.source
      const expanded = yield* Effect.promise(() =>
        ConfigVariable.substitute(
          "path" in options ? { text, type: "path", path: options.path } : { text, type: "virtual", ...options },
        ),
      )
      const parsed = ConfigParse.jsonc(expanded, source)
      const data = ConfigParse.effectSchema(Info, normalizeLoadedConfig(parsed, source), source)
      if (!("path" in options)) return data

      yield* Effect.promise(() => resolveLoadedPlugins(data, options.path))
      // infcode_change start
      // File-based schema injection is disabled because InfCode no longer
      // relies on user-editable JSON config files.
      return data
    })
    // infcode_change end

    const loadFile = Effect.fnUntraced(function* (filepath: string) {
      log.info("loading", { path: filepath })
      const text = yield* readConfigFile(filepath)
      if (!text) return {} as Info
      return yield* loadConfig(text, { path: filepath })
    })

    const loadGlobal = Effect.fnUntraced(function* () {
      // yield* Effect.promise(() => KilocodeConfig.migrateBashPermission()) // infcode_change
      let result: Info = {}
      // infcode_change start
      const terminalRun = TerminalRunSettingsStore.getOrCreateDefault()
      result = mergeConfig(result, { terminalRun } as Info)
      setRuntimeTerminalRunConfig({ terminalRun } as Info)
      result.power = yield* Effect.promise(() => PowerService.saved()) // infcode_change
      const codeReview = CodeReviewSettingsStore.get()
      if (codeReview) result = mergeConfig(result, { codeReview } as Info)
      // infcode_change end
      //infcode_change_start
      // result = mergeConfig(result, yield* loadFile(path.join(Global.Path.config, "config.json")))
      // result = mergeConfig(result, yield* loadFile(path.join(Global.Path.config, "infcode.json")))
      // result = mergeConfig(result, yield* loadFile(path.join(Global.Path.config, "infcode.jsonc")))
      //infcode_change_end
      // result = mergeConfig(result, yield* loadFile(path.join(Global.Path.config, "opencode.json"))) // infcode_change
      // result = mergeConfig(result, yield* loadFile(path.join(Global.Path.config, "opencode.jsonc"))) // infcode_change

      //infcode_change_start
      // const legacy = path.join(Global.Path.config, "config")
      // if (existsSync(legacy)) {
      //   yield* Effect.promise(() =>
      //     import(pathToFileURL(legacy).href, { with: { type: "toml" } })
      //       .then(async (mod) => {
      //         const { provider, model, ...rest } = mod.default
      //         if (provider && model) result.model = `${provider}/${model}`
      //         result = mergeConfig(result, rest)
      //         await fsNode.writeFile(path.join(Global.Path.config, "config.json"), JSON.stringify(result, null, 2))
      //         await fsNode.unlink(legacy)
      //       })
      //       .catch(() => {}),
      //   )
      // }
      //infcode_change_end

      return result
    })

    const [cachedGlobal, invalidateGlobal] = yield* Effect.cachedInvalidateWithTTL(
      loadGlobal().pipe(
        Effect.tapError((error) =>
          Effect.sync(() => log.error("failed to load global config, using defaults", { error: String(error) })),
        ),
        Effect.orElseSucceed((): Info => ({})),
      ),
      Duration.infinity,
    )

    const getGlobal = Effect.fn("Config.getGlobal")(function* () {
      // infcode_change start
      const global = yield* cachedGlobal
      return mergeRuntimeGlobal(global, runtimeTerminalRunPermissionInfo())
      // infcode_change end
    })

    const ensureGitignore = Effect.fn("Config.ensureGitignore")(function* (dir: string) {
      const gitignore = path.join(dir, ".gitignore")
      const hasIgnore = yield* fs.existsSafe(gitignore)
      if (!hasIgnore) {
        yield* fs
          .writeFileString(
            gitignore,
            // kilocode_change start - added pnpm-lock.yaml and yarn.lock (not in upstream)
            [
              "node_modules",
              "package.json",
              "package-lock.json",
              "pnpm-lock.yaml",
              "bun.lock",
              "yarn.lock",
              ".gitignore",
            ].join("\n"),
            // kilocode_change end
          )
          .pipe(
            Effect.catchIf(
              (e) => e.reason._tag === "PermissionDenied" || e.reason._tag === "NotFound", // kilocode_change - also ignore NotFound (broken symlink/junction on Windows)
              () => Effect.void,
            ),
          )
      }
    })

    const loadInstanceState = Effect.fn("Config.loadInstanceState")(
      function* (ctx: InstanceContext) {
        // kilocode_change start - warning accumulator and legacy Kilo config
        const warnings: Warning[] = []
        const auth = yield* authSvc.all().pipe(Effect.orDie)

        let result: Info = {}
        //infcode_change_start
        // const legacy = yield* Effect.promise(() =>
        //   KilocodeConfig.loadLegacyConfigs({
        //     projectDir: ctx.directory,
        //     merge: mergeConfigConcatArrays,
        //   }),
        // )
        // result = mergeConfigConcatArrays(result, legacy.config)
        // warnings.push(...legacy.warnings)

        // const orgModes = yield* Effect.promise(() => KilocodeConfig.loadOrganizationModes(auth))
        // if (Object.keys(orgModes.agents).length > 0) {
        //   result = mergeConfigConcatArrays(result, { agent: orgModes.agents })
        // }
        // warnings.push(...orgModes.warnings)
        //infcode_change_end
        // kilocode_change end

        const consoleManagedProviders = new Set<string>()
        let activeOrgName: string | undefined

        const pluginScopeForSource = Effect.fnUntraced(function* (source: string) {
          if (source.startsWith("http://") || source.startsWith("https://")) return "global"
          // if (source === "INFCODE_CONFIG_CONTENT") return "local" //infcode_change
          if (containsPath(source, ctx)) return "local"
          return "global"
        })

        const mergePluginOrigins = Effect.fnUntraced(function* (
          source: string,
          // mergePluginOrigins receives raw Specs from one config source, before provenance for this merge step
          // is attached.
          list: ConfigPlugin.Spec[] | undefined,
          // Scope can be inferred from the source path, but some callers already know whether the config should
          // behave as global or local and can pass that explicitly.
          kind?: ConfigPlugin.Scope,
        ) {
          if (!list?.length) return
          const hit = kind ?? (yield* pluginScopeForSource(source))
          // Merge newly seen plugin origins with previously collected ones, then dedupe by plugin identity while
          // keeping the winning source/scope metadata for downstream installs, writes, and diagnostics.
          const plugins = ConfigPlugin.deduplicatePluginOrigins([
            ...(result.plugin_origins ?? []),
            ...list.map((spec) => ({ spec, source, scope: hit })),
          ])
          result.plugin = plugins.map((item) => item.spec)
          result.plugin_origins = plugins
        })

        // kilocode_change start
        const merge = Effect.fnUntraced(function* (source: string, next: Info, kind?: ConfigPlugin.Scope) {
          const scope = kind ?? (yield* pluginScopeForSource(source))
          const scoped = scopeIndexing(next, scope) //infcode_change
          result = mergeConfigConcatArrays(result, scoped)
          return yield* mergePluginOrigins(source, scoped.plugin, scope)
        })
        // kilocode_change end

        for (const [key, value] of Object.entries(auth)) {
          if (value.type === "wellknown") {
            const url = key.replace(/\/+$/, "")
            const source = `${url}/.well-known/opencode`
            process.env[value.key] = value.token
            //infcode_change_start
            // log.debug("fetching remote config", { url: source })
            // // kilocode_change start - warn instead of fail on wellknown errors
            // const next = yield* Effect.tryPromise({
            //   try: async () => {
            //     const response = await fetch(source)
            //     if (!response.ok) {
            //       throw new Error(`failed to fetch remote config from ${url}: ${response.status}`)
            //     }
            //     const wellknown = (await response.json()) as { config?: Record<string, unknown> }
            //     const remoteConfig = wellknown.config ?? {}
            //     return remoteConfig
            //   },
            //   catch: (err) => err,
            // }).pipe(
            //   Effect.flatMap((remoteConfig) =>
            //     loadConfig(JSON.stringify(remoteConfig), {
            //       dir: path.dirname(source),
            //       source,
            //     }),
            //   ),
            //   Effect.tap(() => Effect.sync(() => log.debug("loaded remote config from well-known", { url }))),
            //   Effect.catch((err: unknown) => {
            //     caughtWarning(warnings, source, err)
            //     log.warn("skipped remote config due to error", { url, err })
            //     return Effect.succeed({} as Info)
            //   }),
            //   Effect.catchDefect((err: unknown) => {
            //     caughtWarning(warnings, source, err)
            //     log.warn("skipped remote config due to error", { url, err })
            //     return Effect.succeed({} as Info)
            //   }),
            // )
            // yield* merge(source, next, "global")
            // // kilocode_change end
            //infcode_change_end
          }
        }

        // kilocode_change start - capture global config failures as warnings
        const global = yield* getGlobal().pipe(
          Effect.catchDefect((err: unknown) => {
            caughtWarning(warnings, "global config", err)
            return Effect.succeed({} as Info)
          }),
        )
        // kilocode_change end

        yield* merge(Global.Path.config, global, "global")

        // infcode_change start
        // File config is disabled in this checkout, but MCP remains an explicit runtime input
        // written by the host under .infcode/mcpServers/mcp.json.
        const infcodeMcp = yield* Effect.promise(() =>
          InfcodeMcpConfig.load({
            projectDir: ctx.directory,
          }),
        )
        // MCP servers are project-scoped only. Do not merge with any mcp
        // config that may have come from global/runtime config layers.
        if (Object.keys(infcodeMcp).length > 0) result.mcp = infcodeMcp
        else delete result.mcp
        // infcode_change end

        //infcode_change_start
        // if (Flag.INFCODE_CONFIG) {
        //   yield* merge(
        //     Flag.INFCODE_CONFIG,
        //     yield* loadFile(Flag.INFCODE_CONFIG).pipe(
        //       Effect.catchDefect((err: unknown) => {
        //         caughtWarning(warnings, Flag.INFCODE_CONFIG!, err)
        //         return Effect.succeed({} as Info)
        //       }),
        //     ),
        //   )
        //   log.debug("loaded custom config", { path: Flag.INFCODE_CONFIG })
        // }
        //infcode_change_end

        //infcode_change_start
        // if (!Flag.INFCODE_DISABLE_PROJECT_CONFIG) {
        //infcode_change_end
        //infcode_change_start
        // for (const name of [
        //   "infcode",
        //   // "opencode",
        // ] as const) {
        //   for (const file of yield* ConfigPaths.files(name, ctx.directory, ctx.worktree).pipe(Effect.orDie)) {
        //     yield* merge(
        //       file,
        //       yield* loadFile(file).pipe(
        //         Effect.catchDefect((err: unknown) => {
        //           caughtWarning(warnings, file, err)
        //           return Effect.succeed({} as Info)
        //         }),
        //       ),
        //       "local",
        //     )
        //   }
        // }
        //infcode_change_end
        // } //infcode_change

        result.agent = result.agent || {}
        result.mode = result.mode || {}
        result.plugin = result.plugin || []

        // const directories = yield* ConfigPaths.directories(ctx.directory, ctx.worktree) //infcode_change
        const directories: string[] = [] //infcode_change

        //infcode_change_start
        // if (Flag.INFCODE_CONFIG_DIR) {
        //   log.debug("loading config from INFCODE_CONFIG_DIR", { path: Flag.INFCODE_CONFIG_DIR })
        // }
        //infcode_change_end

        const deps: Fiber.Fiber<void, never>[] = []

        // kilocode_change start
        for (const dir of unique(directories)) {
          //infcode_change_start
          // if (KilocodeConfig.isConfigDir(dir)) {
          //   for (const file of KilocodeConfig.ALL_CONFIG_FILES) {
          //     const source = path.join(dir, file)
          //     log.debug(`loading config from ${source}`)
          //     yield* merge(
          //       source,
          //       yield* loadFile(source).pipe(
          //         Effect.catchDefect((err: unknown) => {
          //           caughtWarning(warnings, source, err)
          //           return Effect.succeed({} as Info)
          //         }),
          //       ),
          //     )
          //     result.agent ??= {}
          //     result.mode ??= {}
          //     result.plugin ??= []
          //   }
          // }
          //infcode_change_end
          // kilocode_change end

          yield* ensureGitignore(dir).pipe(Effect.orDie)

          const dep = yield* npmSvc
            .install(dir, {
              add: [
                {
                  name: "@kilocode/plugin",
                  version: InstallationLocal ? undefined : InstallationVersion,
                },
              ],
            })
            .pipe(
              Effect.exit,
              Effect.tap((exit) =>
                Exit.isFailure(exit)
                  ? Effect.sync(() => {
                      log.warn("background dependency install failed", { dir, error: String(exit.cause) })
                    })
                  : Effect.void,
              ),
              Effect.asVoid,
              Effect.forkDetach,
            )
          deps.push(dep)

          // kilocode_change start - propagate parse errors to the Warning accumulator
          //infcode_change_start
          // result.command = mergeDeep(
          //   result.command ?? {},
          //   yield* Effect.promise(() => ConfigCommand.load(dir, warnings)),
          // )
          // result.agent = mergeDeep(result.agent ?? {}, yield* Effect.promise(() => ConfigAgent.load(dir, warnings)))
          // result.agent = mergeDeep(result.agent ?? {}, yield* Effect.promise(() => ConfigAgent.loadMode(dir, warnings)))
          //infcode_change_end
          // kilocode_change end
          // Auto-discovered plugins under InfCode config dirs are already local files, so ConfigPlugin.load // infcode_change
          // returns normalized Specs and we only need to attach origin metadata here.
          // const list = yield* Effect.promise(() => ConfigPlugin.load(dir)) //infcode_change
          // yield* mergePluginOrigins(dir, list) //infcode_change
        }

        //infcode_change_start
        // if (process.env.INFCODE_CONFIG_CONTENT) {
        //   const source = "INFCODE_CONFIG_CONTENT"
        //   yield* merge(
        //     source,
        //     yield* loadConfig(process.env.INFCODE_CONFIG_CONTENT, {
        //       dir: ctx.directory,
        //       source,
        //     }).pipe(
        //       Effect.tap(() => Effect.sync(() => log.debug("loaded custom config from INFCODE_CONFIG_CONTENT"))),
        //       Effect.catchDefect((err: unknown) => {
        //         caughtWarning(warnings, source, err)
        //         return Effect.succeed({} as Info)
        //       }),
        //     ),
        //     "local",
        //   )
        // }
        //infcode_change_end

        const activeAccount = Option.getOrUndefined(
          yield* accountSvc.active().pipe(Effect.catch(() => Effect.succeed(Option.none()))),
        )
        if (activeAccount?.active_org_id) {
          const accountID = activeAccount.id
          const orgID = activeAccount.active_org_id
          const url = activeAccount.url
          yield* Effect.gen(function* () {
            //infcode_change_start
            // const [configOpt, tokenOpt] = yield* Effect.all(
            //   [accountSvc.config(accountID, orgID), accountSvc.token(accountID)],
            //   { concurrency: 2 },
            // )
            //infcode_change_end
            const tokenOpt = yield* accountSvc.token(accountID) //infcode_change
            if (Option.isSome(tokenOpt)) {
              process.env["KILO_CONSOLE_TOKEN"] = tokenOpt.value
              yield* env.set("KILO_CONSOLE_TOKEN", tokenOpt.value)
            }

            //infcode_change_start
            // if (Option.isSome(configOpt)) {
            //   const source = `${url}/api/config`
            //   const next = yield* loadConfig(JSON.stringify(configOpt.value), {
            //     dir: path.dirname(source),
            //     source,
            //   })
            //   for (const providerID of Object.keys(next.provider ?? {})) {
            //     consoleManagedProviders.add(providerID)
            //   }
            //   yield* merge(source, next, "global")
            // }
            //infcode_change_end
          }).pipe(
            Effect.withSpan("Config.loadActiveOrgConfig"),
            Effect.catch((err) => {
              log.debug("failed to fetch remote account config", {
                error: err instanceof Error ? err.message : String(err),
              })
              return Effect.void
            }),
          )
        }

        // const managedDir = ConfigManaged.managedConfigDir() //infcode_change
        // kilocode_change start - include kilo.json/kilo.jsonc in managed dir loading
        //infcode_change_start
        // if (existsSync(managedDir)) {
        //   for (const file of KilocodeConfig.ALL_CONFIG_FILES) {
        //     const source = path.join(managedDir, file)
        //     yield* merge(source, yield* loadFile(source), "global")
        //   }
        // }
        //infcode_change_end
        // kilocode_change end

        // macOS managed preferences (.mobileconfig deployed via MDM) override everything
        // kilocode_change start
        //infcode_change_start
        // const managed = yield* Effect.promise(() => ConfigManaged.readManagedPreferences())
        // if (managed) {
        //   yield* merge(
        //     managed.source,
        //     yield* loadConfig(managed.text, {
        //       dir: path.dirname(managed.source),
        //       source: managed.source,
        //     }),
        //     "global",
        //   )
        // }
        //infcode_change_end
        // kilocode_change end

        for (const [name, mode] of Object.entries(result.mode ?? {})) {
          result.agent = mergeDeep(result.agent ?? {}, {
            [name]: {
              ...mode,
              mode: "primary" as const,
            },
          })
        }

        if (Flag.KILO_PERMISSION) {
          result.permission = mergeDeep(result.permission ?? {}, JSON.parse(Flag.KILO_PERMISSION))
        }

        if (result.tools) {
          const perms: Record<string, ConfigPermission.Action> = {}
          for (const [tool, enabled] of Object.entries(result.tools)) {
            const action: ConfigPermission.Action = enabled ? "allow" : "deny"
            if (tool === "write" || tool === "edit" || tool === "patch") {
              perms.edit = action
              continue
            }
            perms[tool] = action
          }
          result.permission = mergeDeep(perms, result.permission ?? {})
        }

        if (!result.username) result.username = os.userInfo().username

        if (result.autoshare === true && !result.share) {
          result.share = "auto"
        }

        if (Flag.KILO_DISABLE_AUTOCOMPACT) {
          result.compaction = { ...result.compaction, auto: false }
        }
        if (Flag.KILO_DISABLE_PRUNE) {
          result.compaction = { ...result.compaction, prune: false }
        }
        // kilocode_change start — inject Kilo default plugins into both plugin list and origins
        KilocodeDefaultPlugins.apply(result, { disabled: Flag.KILO_DISABLE_DEFAULT_PLUGINS, log })
        // kilocode_change end

        return {
          config: result,
          directories,
          deps,
          warnings, // kilocode_change
          consoleState: {
            consoleManagedProviders: Array.from(consoleManagedProviders),
            activeOrgName,
            switchableOrgCount: 0,
          },
        }
      },
      Effect.provideService(AppFileSystem.Service, fs),
    )

    const state = yield* InstanceState.make<State>(
      Effect.fn("Config.state")(function* (ctx) {
        // infcode_change start - capture before loading. If an update races the
        // load, the next read observes a revision mismatch and retries.
        const currentRevision = revision()
        const loaded = yield* loadInstanceState(ctx).pipe(Effect.orDie)
        return { ...loaded, revision: currentRevision, refresh: Semaphore.makeUnsafe(1) }
        // infcode_change end
      }),
    )

    // infcode_change start - stale refreshes are single-flight per directory.
    // The normal cached path is lock-free; only the first read after a revision
    // takes the semaphore and rebuilds the directory-scoped Config state.
    const currentState = Effect.fn("Config.currentState")(function* () {
      const cached = yield* InstanceState.get(state)
      if (cached.revision === revision()) return cached
      return yield* cached.refresh.withPermits(1)(
        Effect.gen(function* () {
          const latest = yield* InstanceState.get(state)
          if (latest.revision === revision()) return latest
          yield* InstanceState.invalidate(state)
          return yield* InstanceState.get(state)
        }),
      )
    })
    // infcode_change end

    const get = Effect.fn("Config.get")(function* () {
      return (yield* currentState()).config // infcode_change
    })

    // infcode_change start
    const directories = Effect.fn("Config.directories")(function* () {
      return (yield* currentState()).directories // infcode_change
    })

    const getConsoleState = Effect.fn("Config.getConsoleState")(function* () {
      return (yield* currentState()).consoleState // infcode_change
    })

    const waitForDependencies = Effect.fn("Config.waitForDependencies")(function* () {
      yield* Effect.forEach((yield* currentState()).deps, Fiber.join, { concurrency: "unbounded" }).pipe(Effect.asVoid) // infcode_change
    })

    // kilocode_change start
    const warnings = Effect.fn("Config.warnings")(function* () {
      return (yield* currentState()).warnings // infcode_change
    })
    // kilocode_change end

    const update = Effect.fn("Config.update")(function* (config: Info, options?: { dispose?: boolean }) {
      //infcode_change_start
      const patch = writable(config)
      if (Object.keys(patch).length > 0) log.warn("ignored project config update because file config is disabled")
      //infcode_change_end
      if (options?.dispose !== false) {
        // Fail loudly if no instance is bound — silently skipping would
        // mask "config update without an active instance" bugs. The throw
        // comes from `Instance.current` inside `InstanceState.context`.
        const ctx = yield* InstanceState.context
        yield* Effect.promise(() => InstanceStore.disposeInstance(ctx))
      }
    })

    const invalidate = Effect.fn("Config.invalidate")(function* (wait?: boolean) {
      yield* invalidateGlobal
      const task = InstanceStore.disposeAllInstances()
        .catch(() => undefined)
        .finally(() =>
          GlobalBus.emit("event", {
            directory: "global",
            payload: {
              type: Event.Disposed.type,
              properties: {},
            },
          }),
        )
      if (wait) yield* Effect.promise(() => task)
      else void task
    })

    // kilocode_change start - add dispose option to skip Instance.disposeAll for permission-only changes
    const updateGlobal = Effect.fn("Config.updateGlobal")(function* (config: Info, options?: { dispose?: boolean }) {
      const configWithoutRuntime = stripTerminalRunRuntime(config)
      const dispose =
        options?.dispose ??
        !(
          terminalRunRuntimeOnly(config) ||
          terminalRunPermissionOnly(configWithoutRuntime) ||
          codeReviewOnly(configWithoutRuntime) ||
          (hasOwn(configWithoutRuntime, "power") && Object.keys(configWithoutRuntime).length === 1)
        ) // infcode_change
      // kilocode_change end
      // infcode_change start
      const patch = writableGlobal(configWithoutRuntime)
      const terminalPatch = terminalRunPermissionPatch(patch)
      const terminalRun = terminalRunRuntimePatch(config)?.terminalRun
      const savedTerminalRun = terminalRun ? TerminalRunSettingsStore.save(terminalRun) : undefined
      const dimensions = config.codeReview?.dimensions
      const savedCodeReview = dimensions ? CodeReviewSettingsStore.save(dimensions) : undefined
      const power = config.power
      if (power) yield* Effect.promise(() => PowerService.save(power.preventSleep))
      if (savedTerminalRun) setRuntimeTerminalRunConfig({ terminalRun: savedTerminalRun } as Info)
      else if (terminalPatch) setRuntimeTerminalRunPermission(terminalPatch)
      if (savedTerminalRun || savedCodeReview || power) yield* invalidateGlobal
      if (hasIgnoredGlobalPatch(patch)) log.warn("ignored global config update because file config is disabled")
      const loaded = yield* getGlobal()
      const next = terminalPatch && !savedTerminalRun ? mergeRuntimeGlobal(loaded, terminalPatch) : loaded
      if (terminalPatch && !savedTerminalRun) setRuntimeTerminalRunPermission(terminalPatch)
      // infcode_change end

      // kilocode_change start - skip dispose when caller opts out
      if (!dispose) {
        if (!(terminalPatch && !savedTerminalRun)) yield* invalidateGlobal // infcode_change
        bumpRevision() // infcode_change - make every Config/Agent layer observe the update lazily
        yield* ignoreMissingInstance(InstanceState.invalidate(state)) // infcode_change
        yield* Effect.sync(() =>
          GlobalBus.emit("event", {
            directory: "global",
            payload: {
              type: Event.ConfigUpdated.type,
              properties: {},
            },
          }),
        )
        return next
      }
      // kilocode_change end

      // infcode_change start
      if (Object.keys(patch).length > 0) yield* invalidate()
      // infcode_change end
      return next
    })

    return Service.of({
      get,
      getGlobal,
      getConsoleState,
      update,
      updateGlobal,
      invalidate,
      directories,
      waitForDependencies,
      warnings, // kilocode_change
    })
  }),
)

export const defaultLayer = layer.pipe(
  Layer.provide(EffectFlock.defaultLayer),
  Layer.provide(AppFileSystem.defaultLayer),
  Layer.provide(Env.defaultLayer),
  Layer.provide(Auth.defaultLayer),
  Layer.provide(Account.defaultLayer),
  Layer.provide(Npm.defaultLayer),
)

// kilocode_change start - keep async wrappers for Kilo callsites during Effect migration
const { runPromise } = makeRuntime(Service, defaultLayer)

export async function get() {
  return runPromise((svc) => svc.get())
}

export async function getGlobal() {
  return runPromise((svc) => svc.getGlobal())
}

export async function getConsoleState() {
  return runPromise((svc) => svc.getConsoleState())
}

export async function update(config: Info, options?: { dispose?: boolean }) {
  return runPromise((svc) => svc.update(config, options))
}

export async function updateGlobal(config: Info, options?: { dispose?: boolean }) {
  return runPromise((svc) => svc.updateGlobal(config, options))
}

export async function invalidate(wait = false) {
  return runPromise((svc) => svc.invalidate(wait))
}

export async function directories() {
  return runPromise((svc) => svc.directories())
}

export async function waitForDependencies() {
  return runPromise((svc) => svc.waitForDependencies())
}

export async function warnings() {
  return runPromise((svc) => svc.warnings())
}
// kilocode_change end

export * as Config from "./config"
